import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { StudyDetailResponse } from '@/api/generated/types.gen';
import { cn } from '@/lib/utils';
import StudyVisibilityControl from '@/components/StudyVisibilityControl';
import type { StudyLibrary } from '../-data/constants';
import StudyKindBadge from './StudyKindBadge';
import StudyStatusBadge from './dashboard/StudyStatusBadge';

interface StudyHeaderBarProps {
  /** The study, or null while it loads — badges hide until it's present. */
  study: StudyDetailResponse | null;
  /** Product library that owns the current detail route. */
  library?: StudyLibrary;
  /** Title node: an editable name in SimOps, a plain heading in the bridge. */
  title: ReactNode;
  /** Rendered after the badges, before the stepper (e.g. the save indicator). */
  trailing?: ReactNode;
  /** Inline journey stepper, pushed to the right. */
  stepper?: ReactNode;
  className?: string;
}

/**
 * The study "identity" row — title + DOE/Draft/Private badges + an inline
 * journey stepper. Shared by the SimOps study header, the EvalTool study
 * header and the MeshGen draft bridge so the three read identically.
 *
 * The title truncates. A long name used to shrink to its longest word and
 * stack one word per line, which blew the header open. Badges stay beside
 * the name; the stepper keeps the right edge and drops to the next row when
 * the bar is narrower than the groups together (a chat panel, a phone).
 */
export default function StudyHeaderBar({
  study,
  library,
  title,
  trailing,
  stepper,
  className,
}: StudyHeaderBarProps) {
  const { t } = useTranslation();

  return (
    <div
      data-slot="study-header-bar"
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}
    >
      <div className="flex max-w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        {library === 'evaltool' ? (
          <Link
            to="/evaltool"
            aria-label={t('simopsShared.studyHeader.backToEvaluations')}
            className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('simopsShared.studyHeader.backToEvaluations')}
          </Link>
        ) : library === 'simops' ? (
          <Link
            to="/simops"
            aria-label={t('simopsShared.studyHeader.backToStudies')}
            className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('simopsShared.studyHeader.backToStudies')}
          </Link>
        ) : null}
        <div className="max-w-md min-w-24 [&_h1]:block [&_h1]:max-w-full [&_h1]:truncate">
          {title}
        </div>
        {study && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <StudyKindBadge study={study} />
            <StudyStatusBadge status={study.status} />
            <StudyVisibilityControl
              studyId={study.id}
              visibility={study.visibility}
              size="sm"
              source="detail"
              myRole={study.my_role}
            />
          </div>
        )}
        {trailing && <div className="max-w-full shrink-0">{trailing}</div>}
      </div>
      {stepper && <div className="ml-auto shrink-0">{stepper}</div>}
    </div>
  );
}
